import * as Dialog from "@radix-ui/react-dialog";
import { useState } from "react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import type { BrandRole } from "@/hooks/use-members";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: { email: string; role: BrandRole }) => Promise<unknown>;
}

export function InviteMemberDialog({ open, onOpenChange, onSubmit }: Props) {
  const { t } = useTranslation("settings");
  const schema = z.object({
    email: z.string().email(t("team.invite.invalidEmail")),
    role: z.enum(["ADMIN", "CREATOR", "REVIEWER", "SCHEDULER", "VIEWER"]),
  });
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<BrandRole>("CREATOR");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = schema.safeParse({ email, role });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({ email, role });
      toast.success(t("team.invite.inviteSent", { email }));
      setEmail("");
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err.message ?? t("team.invite.failedSend"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-background p-6 shadow-lg">
          <Dialog.Title className="text-lg font-semibold">{t("team.invite.title")}</Dialog.Title>
          <Dialog.Description className="text-sm text-muted-foreground">{t("team.invite.description")}</Dialog.Description>
          <form onSubmit={handleSubmit} className="mt-4 space-y-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("team.invite.emailPlaceholder")}
              className="block w-full rounded border px-3 py-2 text-sm"
              required
            />
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as BrandRole)}
              className="block w-full rounded border px-3 py-2 text-sm"
            >
              <option value="ADMIN">{t("team.roles.ADMIN")}</option>
              <option value="CREATOR">{t("team.roles.CREATOR")}</option>
              <option value="REVIEWER">{t("team.roles.REVIEWER")}</option>
              <option value="SCHEDULER">{t("team.roles.SCHEDULER")}</option>
              <option value="VIEWER">{t("team.roles.VIEWER")}</option>
            </select>
            <div className="flex justify-end gap-2">
              <Dialog.Close asChild>
                <Button type="button" variant="outline">{t("actions.cancel", { ns: "common" })}</Button>
              </Dialog.Close>
              <Button type="submit" disabled={submitting}>
                {submitting ? t("team.invite.sending") : t("team.invite.sendInvite")}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
